import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { TaskService } from '../../../../shared/service/filter/task.service';
import { SiteService } from '../../../../shared/service/filter/site.service';
import { forkJoin, from, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { GetManyResponseInterface } from '../../../../shared/model/interface/crud-response-interface.model';
import { ApiAutomationSettingsUtilities } from '../../../../view/settings/api-automation-settings/api-automation-settings.utilities';
import {
  EApiAutomationResource,
  ETagType,
  IApiAutomationOption,
  TAG_COMBINATION_DESTINATION_RESOURCE,
  TAG_TYPE_DESTINATION_RESOURCE,
} from './api-automation.model';

interface ILookupRow {
  id: number;
  name?: string;
  description?: string;
  tagCombinations?: string;
  tagName?: string;
  sensorId?: string;
  heartbeatId?: string;
}

/**
 * Since the OPC tag automation backend was reworked, only tag definitions and burst settings carry
 * site/line context; every other table is a plain rule row that other tables reach by id. This
 * service turns those tables into `{ id, name }` dropdown options so the editors can offer a picker
 * for `destinationTableId`, `heartbeatSettingId`, `lineDestinationTableId` and friends.
 */
@Injectable({
  providedIn: 'root',
})
export class ApiAutomationLookupService {
  private readonly BASE_URL = `${this.baseUrl}/api-automation`;
  private readonly OPTIONS_LIMIT: string = '1000';
  private readonly DEFAULT_SORT: string = 'id,DESC';

  /** Resources whose pickers are ordered differently from the newest-first default. */
  private readonly SORT_OF_RESOURCE: Partial<Record<EApiAutomationResource, string>> = {
    [EApiAutomationResource.loadBalancerChannels]: 'id,ASC',
  };

  constructor(
    public http: HttpClient,
    @Inject('API_BASE_URL') private readonly baseUrl: string,
    private readonly taskService: TaskService,
    private readonly siteService: SiteService,
  ) {}

  public getOptions(resource: EApiAutomationResource, params?: HttpParams): Observable<IApiAutomationOption[]> {
    const httpParams: HttpParams = (params ?? new HttpParams())
      .set('limit', this.OPTIONS_LIMIT)
      .set('sort', this.SORT_OF_RESOURCE[resource] ?? this.DEFAULT_SORT);

    return this.http
      .get<GetManyResponseInterface<ILookupRow>>(`${this.BASE_URL}/${resource}`, { params: httpParams })
      .pipe(map((response) => (response?.data ?? []).map((row) => this.toOption(row))));
  }

  /**
   * Returns the option list for whichever table `destinationTableId` points at for the given tag
   * type, or an empty list when the tag type has no destination table.
   */
  public getDestinationOptions(tagType: ETagType | string | null): Observable<IApiAutomationOption[]> {
    const resource: EApiAutomationResource | undefined = TAG_TYPE_DESTINATION_RESOURCE[tagType as ETagType];

    return resource ? this.getOptions(resource) : of([] as IApiAutomationOption[]);
  }

  /**
   * `tag_combinations.destinationTableId` points at change_activity for `activityChange` and at
   * change_station_activity for `stationChange`; no other tag type carries one.
   */
  public getTagCombinationDestinationOptions(tagType: ETagType | string | null): Observable<IApiAutomationOption[]> {
    const resource: EApiAutomationResource | undefined = TAG_COMBINATION_DESTINATION_RESOURCE[tagType as ETagType];

    return resource ? this.getOptions(resource) : of([] as IApiAutomationOption[]);
  }

  /**
   * A datatable row's destination lives in whichever table its own `tagType` points at, so one
   * option list cannot label a grid of mixed tag types. This resolves the distinct tag types on a
   * page into a `tagType -> options` map, fetching each underlying table only once even when
   * several tag types share it (`failureReason` and `stationFailureReason` both use
   * fault_code_mapping, for instance).
   */
  public getDestinationOptionsMap(
    tagTypes: (ETagType | string)[],
    forTagCombination: boolean = false,
  ): Observable<Record<string, IApiAutomationOption[]>> {
    const resourceOfType: Readonly<Partial<Record<ETagType, EApiAutomationResource>>> = forTagCombination
      ? TAG_COMBINATION_DESTINATION_RESOURCE
      : TAG_TYPE_DESTINATION_RESOURCE;

    const resourceOfTagType = new Map<string, EApiAutomationResource>();

    for (const tagType of tagTypes) {
      const resource: EApiAutomationResource | undefined = resourceOfType[tagType as ETagType];

      if (resource) {
        resourceOfTagType.set(String(tagType), resource);
      }
    }

    const resources: EApiAutomationResource[] = [...new Set(resourceOfTagType.values())];

    if (!resources.length) {
      return of({});
    }

    return forkJoin(resources.map((resource) => this.getOptions(resource))).pipe(
      map((results) => {
        const optionsOfResource = new Map<EApiAutomationResource, IApiAutomationOption[]>(
          resources.map((resource, index) => [resource, results[index]]),
        );

        return Object.fromEntries(
          [...resourceOfTagType].map(([tagType, resource]) => [tagType, optionsOfResource.get(resource) ?? []]),
        );
      }),
    );
  }

  /**
   * Task pickers are scoped to a single activity, so they cannot label a grid whose rows point at
   * tasks under different activities. Task ids are globally unique, so the grid is resolved by id
   * in one request instead of one request per activity.
   */
  public getTaskOptions(taskIds: number[]): Observable<IApiAutomationOption[]> {
    const ids: number[] = [...new Set(taskIds.filter((id) => id !== null && id !== undefined))];

    if (!ids.length) {
      return of([] as IApiAutomationOption[]);
    }

    const params: HttpParams = new HttpParams()
      .set('s', JSON.stringify({ id: { $in: ids } }))
      .set('limit', this.OPTIONS_LIMIT)
      .set('join', 'line||title');

    return from(this.taskService.getData(params)).pipe(
      map((tasks) => (tasks ?? []).map((task) => ApiAutomationSettingsUtilities.toTaskOption(task))),
    );
  }

  public getSiteOptions(): Observable<IApiAutomationOption[]> {
    const params: HttpParams = new HttpParams().set('fields', 'id,name').set('limit', this.OPTIONS_LIMIT);

    return this.siteService
      .getSites(params)
      .pipe(
        map((response) =>
          ApiAutomationSettingsUtilities.sortOptionsByName(
            (response?.data ?? []).map((site) => ({ id: Number(site.id), name: site.name })),
          ),
        ),
      );
  }

  /** Active tasks of a site, labelled "Line - Task" so same-titled tasks on different lines stay apart. */
  public getSiteTaskOptions(siteId: number): Observable<IApiAutomationOption[]> {
    const params: HttpParams = new HttpParams()
      .set('s', JSON.stringify({ statusId: 1, siteId }))
      .set('limit', this.OPTIONS_LIMIT)
      .set('join', 'line||title');

    return from(this.taskService.getData(params)).pipe(
      map((tasks) =>
        ApiAutomationSettingsUtilities.sortOptionsByName(
          (tasks ?? []).map((task) => ApiAutomationSettingsUtilities.toTaskOption(task)),
        ),
      ),
    );
  }

  private toOption(row: ILookupRow): IApiAutomationOption {
    const label: string =
      row.name ?? row.tagName ?? row.sensorId ?? row.heartbeatId ?? row.description ?? row.tagCombinations ?? '';

    return { id: row.id, name: label ? `#${row.id} - ${label}` : `#${row.id}` };
  }
}
